import { useCallback, useRef, useState, type CSSProperties } from 'react'
import { useEnrollmentStore } from '@/stores'
import type { EligibilityFormData } from '../types'
import { formatPhoneNumber, formatSsnInput, getDigitsOnly, isMaskedStoredSsn } from './helpers'

type UseContactDetailsStepArgs = {
  formData: EligibilityFormData
  onBack: () => void
  updateFormData: (data: Partial<EligibilityFormData>) => void
}

export function useContactDetailsStep({
  formData,
  onBack,
  updateFormData
}: UseContactDetailsStepArgs) {
  const initialSsnValueRef = useRef(formData.ssn)
  const storedPhoneNumber =
    useEnrollmentStore(state => state.formData.phoneNumber) ||
    useEnrollmentStore(state => state.formData.phone)
  const emergencyContactMatchesApplicantPhone =
    getDigitsOnly(formData.emergencyContactPhone).length > 0 &&
    getDigitsOnly(formData.emergencyContactPhone) === getDigitsOnly(storedPhoneNumber)
  const emergencyContactPhoneDigits = getDigitsOnly(formData.emergencyContactPhone)
  const ssnDigits = getDigitsOnly(formData.ssn)
  const hasMaskedStoredSsn = isMaskedStoredSsn(formData.ssn)
  const hasValidSsnValue = hasMaskedStoredSsn || ssnDigits.length === 9
  const ssnLengthError =
    ssnDigits.length > 0 && !hasMaskedStoredSsn && ssnDigits.length !== 9
      ? 'Social Security Number must contain 9 digits.'
      : null

  const [showSsn, setShowSsn] = useState(false)
  const maskedSsnInputStyle = showSsn
    ? undefined
    : ({ WebkitTextSecurity: 'disc' } as CSSProperties)

  const handleBackClick = useCallback(() => {
    if (formData.ssn !== initialSsnValueRef.current) {
      updateFormData({ ssn: initialSsnValueRef.current })
    }

    onBack()
  }, [formData.ssn, onBack, updateFormData])

  return {
    emergencyContactMatchesApplicantPhone,
    emergencyContactPhoneDigits,
    handleBackClick,
    hasValidSsnValue,
    maskedSsnInputStyle,
    ssnLengthError,
    showSsn,
    setShowSsn,
    handleSsnChange: (value: string) =>
      updateFormData({ ssn: formatSsnInput(value, formData.ssn) }),
    handleEmergencyContactNameChange: (value: string) =>
      updateFormData({ emergencyContactName: value }),
    handleEmergencyContactPhoneChange: (value: string) =>
      updateFormData({ emergencyContactPhone: formatPhoneNumber(value) })
  }
}
